import { apiSlice } from './apiSlice';

export interface MfaStatus {
  isMfaEnabled: boolean;
  activeMethod: string | null;
  totp: { configured: boolean; pending: boolean };
}

export interface MfaSetupResponse {
  qrCode: string;
  secret: string;
}

export interface ResultManagerMfaSetup extends MfaSetupResponse {
  accountName: string;
}

export interface ResultManagerListItem {
  id: string;
  username: string;
  name: string;
  status: string;
  isMfaEnabled: boolean;
  mfaPending: boolean;
  mfaMethod: string | null;
}

interface ResultManagerListResponse {
  success: boolean;
  items: ResultManagerListItem[];
  pagination: { currentPage: number; totalPage: number; totalItems: number; limit: number };
}

type MfaAccount = { userId: string; role?: string };

export const mfaApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getMfaStatus: builder.query<MfaStatus, void>({
      query: () => ({ url: '/mfa/status', method: 'GET' }),
    }),
    enableMfa: builder.mutation<MfaSetupResponse, { usernameOrEmail: string; password: string }>({
      query: (body) => ({
        url: '/mfa/setup',
        method: 'POST',
        body: { status: 'ACTIVE', ...body },
      }),
    }),
    disableMfa: builder.mutation<{ status: 'INACTIVE' }, { token: string }>({
      query: (body) => ({
        url: '/mfa/setup',
        method: 'POST',
        body: { status: 'INACTIVE', ...body },
      }),
    }),
    confirmMfa: builder.mutation<{ status: 'ACTIVE'; method: string }, { token: string }>({
      query: (body) => ({
        url: '/mfa/setup/confirm',
        method: 'POST',
        body,
      }),
    }),
    getOwnerMfaStatus: builder.query<MfaStatus, MfaAccount>({
      query: ({ userId, role }) => ({
        url: '/mfa/owner/status',
        method: 'GET',
        params: { userId, ...(role ? { role } : {}) },
      }),
    }),
    ownerDisableMfa: builder.mutation<unknown, MfaAccount>({
      query: (body) => ({
        url: '/mfa/owner/disable',
        method: 'POST',
        body,
      }),
    }),
    getResultManagers: builder.query<
      ResultManagerListResponse,
      { search?: string; page: number; limit: number }
    >({
      query: ({ search, page, limit }) => ({
        url: '/mfa/result-manager/list',
        method: 'GET',
        params: { ...(search ? { search } : {}), page, limit },
      }),
      providesTags: ['ResultManagerMfa'],
    }),
    getResultManagerMfaStatus: builder.query<MfaStatus, { userId: string }>({
      query: ({ userId }) => ({
        url: '/mfa/result-manager/status',
        method: 'GET',
        params: { userId },
      }),
    }),
    setupResultManagerMfa: builder.mutation<ResultManagerMfaSetup, { userId: string }>({
      query: (body) => ({
        url: '/mfa/result-manager/setup',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ResultManagerMfa'],
    }),
    confirmResultManagerMfa: builder.mutation<
      { status: 'ACTIVE'; method: string },
      { userId: string; token: string }
    >({
      query: (body) => ({
        url: '/mfa/result-manager/confirm',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ResultManagerMfa'],
    }),
    disableResultManagerMfa: builder.mutation<{ status: 'INACTIVE' }, { userId: string }>({
      query: (body) => ({
        url: '/mfa/result-manager/disable',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ResultManagerMfa'],
    }),
  }),
});

export const {
  useGetMfaStatusQuery,
  useEnableMfaMutation,
  useDisableMfaMutation,
  useConfirmMfaMutation,
  useLazyGetOwnerMfaStatusQuery,
  useOwnerDisableMfaMutation,
  useGetResultManagersQuery,
  useGetResultManagerMfaStatusQuery,
  useSetupResultManagerMfaMutation,
  useConfirmResultManagerMfaMutation,
  useDisableResultManagerMfaMutation,
} = mfaApi;
